import { z } from 'zod';

/** Roles de membresía (§4.3 del plan). `superadmin` NO es un rol: es `users.is_superadmin`. */
export const ROLES = [
  'owner',
  'admin',
  'operator',
  'viewer',
  'client_admin',
  'client_viewer',
] as const;
export const roleSchema = z.enum(ROLES);
export type Role = z.infer<typeof roleSchema>;

export const SCOPE_TYPES = ['agency', 'client', 'tenant'] as const;
export const scopeTypeSchema = z.enum(SCOPE_TYPES);
export type ScopeType = z.infer<typeof scopeTypeSchema>;

export const PERMISSIONS = [
  'data:read',
  'inbox:read',
  'inbox:write',
  'leads:write',
  /** Overrides de score/recomendación/personalización y re-encolar: sólo roles de agencia. */
  'leads:override',
  'prompts:read',
  'prompts:write',
  'ops:read',
  'config:read',
  'config:write',
  'users:invite',
  'billing:read',
  'tenants:delete',
] as const;
export type Permission = (typeof PERMISSIONS)[number];

const AGENCY_READ: Permission[] = [
  'data:read',
  'inbox:read',
  'prompts:read',
  'ops:read',
  'config:read',
];

export const ROLE_PERMISSIONS: Record<Role, readonly Permission[]> = {
  owner: [...PERMISSIONS],
  admin: PERMISSIONS.filter((p) => p !== 'tenants:delete'),
  operator: [...AGENCY_READ, 'inbox:write', 'leads:write', 'leads:override', 'prompts:write'],
  viewer: AGENCY_READ,
  client_admin: [
    'data:read',
    'inbox:read',
    'inbox:write',
    'leads:write',
    'prompts:read',
    'prompts:write',
    'users:invite',
    'billing:read',
  ],
  client_viewer: ['data:read', 'inbox:read'],
};

/** Orden de "más poder" a "menos", para quedarse con el rol más alto cuando hay varias membresías. */
export const ROLE_RANK: Record<Role, number> = {
  owner: 60,
  admin: 50,
  operator: 40,
  client_admin: 30,
  viewer: 20,
  client_viewer: 10,
};

/**
 * Roles de agencia: 2FA obligatorio (§9), en cualquier alcance. Los de cliente lo tienen opcional.
 * Además, TODA membresía de alcance agencia lo exige (ver `mfaRequired` en la API).
 */
export const MFA_REQUIRED_ROLES: readonly Role[] = ['owner', 'admin', 'operator', 'viewer'];

/** Qué roles puede tener cada tipo de alcance. owner/admin sólo existen a nivel agencia. */
export const ROLES_BY_SCOPE: Record<ScopeType, readonly Role[]> = {
  agency: ['owner', 'admin', 'operator', 'viewer'],
  client: ['operator', 'viewer', 'client_admin', 'client_viewer'],
  tenant: ['operator', 'viewer', 'client_admin', 'client_viewer'],
};

/** Roles que un client_admin puede repartir (sólo del lado cliente). */
export const CLIENT_ASSIGNABLE_ROLES: readonly Role[] = ['client_admin', 'client_viewer'];

export function permissionsFor(role: Role): readonly Permission[] {
  return ROLE_PERMISSIONS[role];
}

export function roleHas(role: Role, permission: Permission): boolean {
  return ROLE_PERMISSIONS[role].includes(permission);
}

export function higherRole(a: Role, b: Role): Role {
  return ROLE_RANK[a] >= ROLE_RANK[b] ? a : b;
}
